/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import { TrashIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useAppDispatch } from 'helpers/store'
import { useTranslation } from 'react-i18next'
import { PackagesCart } from 'types/entities/PackagesCart'
import { currencyNumberFormat } from 'utils/utility'
import { OfferCalculation } from 'types/entities/OfferCalculation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'utils/toastUtils'
import { OffersAction } from 'store/offers/OffersAction'
import {
    BookingSellerVoucherInputs,
    useBookingSellerVoucherSchema,
} from 'pages/Front/Booking/Seller/schemas/useBookingSellerVoucherSchema'
import UiButton, { UiButtonStyle } from 'components/Ui/UiButton'
import UiInput from 'components/Ui/UiInput'
import { useVouchers } from 'utils/voucherUtils'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'

interface EditCalculationDiscountModalProps {
    calculation: OfferCalculation
    packagesCart: PackagesCart | undefined
    offerId: number
    sellerId: number
    voucher_id?: number
    closeModal?: () => void
}

interface DiscountRowProps {
    label: string
    value: string
    className?: string
    valueClassName?: string
}

const DiscountRow: React.FC<DiscountRowProps> = ({
    label,
    value,
    className,
    valueClassName,
}) => (
    <tr
        className={clsx(
            'border-b border-dashed border-gray-200 dark:border-gray-700',
            className
        )}
    >
        <td className={clsx('font-medium text-gray-700 dark:text-gray-300')}>
            {label}
        </td>
        <td
            className={clsx(
                'text-right text-gray-900 dark:text-gray-100',
                valueClassName
            )}
        >
            {value}
        </td>
    </tr>
)

const EditCalculationDiscountModal = (props: EditCalculationDiscountModalProps) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const [isLoading, setIsLoading] = React.useState<boolean>(false)

    // Sum vouchers by voucher_id to handle multiple vouchers
    const { getVoucher } = useVouchers(props.packagesCart)

    // Determine if we're in edit mode: voucher_id provided and voucher exists
    const currentVoucher = props.voucher_id ? getVoucher(props.voucher_id) : null
    const isEditMode = !!currentVoucher

    // prices.total already includes the current discount — add it back when editing
    // so "Total price" / "Price after discount" use the pre-discount base
    const discountedTotal = props.packagesCart?.prices?.total || 0
    const currentDiscountAmount = currentVoucher?.price || 0
    const totalPrice = discountedTotal + (isEditMode ? currentDiscountAmount : 0)

    const BookingSellerVoucherSchema = useBookingSellerVoucherSchema()
    const { register, setValue, watch } = useForm<BookingSellerVoucherInputs>({
        resolver: zodResolver(BookingSellerVoucherSchema),
        defaultValues: {
            priceDiscount: isEditMode && currentVoucher?.price ? currentVoucher.price / 100 : 0,
        },
    })

    const priceDiscount = watch('priceDiscount')
    const voucherPriceDiscount = Number(priceDiscount) || 0
    const priceAfterDiscount = totalPrice - voucherPriceDiscount * 100

    const successMessage = isEditMode ? 'Discount updated' : 'Discount added'
    const errorMessage = isEditMode
        ? 'Failed to update discount'
        : 'Failed to add discount'

    const applyDiscountHandler = () => {
        if (isLoading) {
            return
        }

        setIsLoading(true)
        const normalizedVoucherPriceDiscount = voucherPriceDiscount * 100

        // Use edit endpoint if in edit mode, otherwise use add endpoint
        const action = isEditMode && props.voucher_id
            ? OffersAction.fetchEditVoucherInCalculation(
                props.packagesCart?.id || 0,
                normalizedVoucherPriceDiscount,
                props.offerId,
                props.sellerId,
                props.voucher_id
            )
            : OffersAction.fetchAddVoucherToCalculation(
                props.packagesCart?.id || 0,
                normalizedVoucherPriceDiscount,
                props.offerId,
                props.sellerId
            )

        dispatch(action)
            .then(() => {
                if (props.closeModal) {
                    props.closeModal()
                }
                toast.success(t(successMessage))
            })
            .catch((error) => {
                console.error(isEditMode ? 'Error updating discount:' : 'Error adding discount:', error)
                toast.error(t(errorMessage))
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const deleteDiscountHandler = () => {
        if (isLoading) {
            return
        }

        if (!props.voucher_id) {
            toast.error(t('No voucher found to remove'))
            return
        }

        setIsLoading(true)
        dispatch(
            OffersAction.fetchDeleteVoucherFromCalculation(
                props.packagesCart?.id || 0,
                props.offerId,
                props.sellerId,
                props.voucher_id,
            )
        )
            .then(() => {
                setValue('priceDiscount', 0)
                if (props.closeModal) {
                    props.closeModal()
                }
                toast.success(t('Discount removed'))
            })
            .catch((error) => {
                console.error('Error removing discount:', error)
                toast.error(t('Failed to remove discount'))
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    return (
        <UiFormModalShell
            className="EditCalculationDiscountModal"
            title={t('Discount')}
            subtitle={props.calculation?.name || undefined}
            showHeader
            bodyClassName="px-4 py-4"
        >
            <div className={clsx('EditCalculationDiscountModal flex flex-col gap-4')}>
                <UiFormInner title={t('Calculation information')} className="border border-gray-200 dark:border-gray-800 rounded-lg px-3 pb-3">
                    <table className={clsx('w-full')}>
                        <tbody>
                            <DiscountRow
                                label={t('Total price')}
                                value={currencyNumberFormat(totalPrice)}
                            />
                            {currentVoucher && (
                                <DiscountRow
                                    label={t('Current discount')}
                                    value={currencyNumberFormat(currentVoucher.price || 0)}
                                />
                            )}
                            {voucherPriceDiscount > 0 && (
                                <DiscountRow
                                    label={t('New discount')}
                                    value={currencyNumberFormat(voucherPriceDiscount * 100)}
                                    className="bg-blue-50 dark:bg-blue-900/20"
                                    valueClassName="font-semibold"
                                />
                            )}
                            {voucherPriceDiscount > 0 && totalPrice > 0 && (
                                <DiscountRow
                                    label={t('Price after discount')}
                                    value={currencyNumberFormat(priceAfterDiscount)}
                                    className="bg-green-50 dark:bg-green-900/20"
                                    valueClassName="font-bold"
                                />
                            )}
                        </tbody>
                    </table>
                </UiFormInner>

                <UiFormInner title={t('Actions')} className="border border-gray-200 dark:border-gray-700 rounded-lg px-3 pb-3">
                    <UiFormGrid cols={1} gap={3}>
                        <UiInput
                            label={t('CHF discount')}
                            {...register('priceDiscount')}
                            type="number"
                            min={0}
                            max={1000}
                            step={0.01}
                            placeholder={currencyNumberFormat(voucherPriceDiscount)}
                            disabled={isLoading}
                        />
                        <div className={clsx('flex gap-2 justify-end')}>
                            {currentVoucher && (
                                <UiButton
                                    style={UiButtonStyle.Red}
                                    onClick={deleteDiscountHandler}
                                    disabled={isLoading}
                                    icon={<TrashIcon className={clsx('w-5 h-5 flex-none')} />}
                                >
                                    {isLoading ? t('Removing...') : t('Remove discount')}
                                </UiButton>
                            )}
                            <UiButton
                                style={UiButtonStyle.Green}
                                onClick={applyDiscountHandler}
                                disabled={isLoading || voucherPriceDiscount <= 0}
                                icon={<CheckIcon className={clsx('w-5 h-5 flex-none')} />}
                            >
                                {isLoading
                                    ? t('Processing...')
                                    : isEditMode
                                      ? t('Update discount')
                                      : t('Apply discount')}
                            </UiButton>
                        </div>
                    </UiFormGrid>
                </UiFormInner>
            </div>
        </UiFormModalShell>
    )
}

export default EditCalculationDiscountModal
